// src/services/ai.service.ts

import { GoogleGenerativeAI } from '@google/generative-ai';
import { GenerateResourceRequest, GenerateResourceResponse } from '../types/resource.types';

// When VITE_GEMINI_API_KEY is set the app calls Gemini directly (no backend needed).
// For local development with the Express backend set VITE_API_URL=http://localhost:5001.
const GEMINI_API_KEY        = import.meta.env.VITE_GEMINI_API_KEY        as string | undefined;
const GROQ_API_KEY          = import.meta.env.VITE_GROQ_API_KEY          as string | undefined;
const DEEPSEEK_API_KEY      = import.meta.env.VITE_DEEPSEEK_API_KEY      as string | undefined;
const OPENROUTER_API_KEY    = import.meta.env.VITE_OPENROUTER_API_KEY    as string | undefined;
const API_URL = import.meta.env.VITE_API_URL ?? '';

// Fallback list if ListModels discovery fails
const GEMINI_MODELS_FALLBACK = [
  'gemini-2.0-flash-001',
  'gemini-2.0-flash-lite-001',
  'gemini-2.0-pro-exp',
  'gemini-exp-1206',
];

function makeGeminiModel(model: string) {
  if (!GEMINI_API_KEY) return null;
  const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
  return genAI.getGenerativeModel(
    { model, generationConfig: { maxOutputTokens: 4096 } },
    { apiVersion: 'v1beta' },
  );
}

// Cache so we only call ListModels once per session
let _availableGeminiModels: string[] | null = null;
async function getAvailableGeminiModels(): Promise<string[]> {
  if (!GEMINI_API_KEY) return [];
  if (_availableGeminiModels !== null) return _availableGeminiModels;
  try {
    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models?key=${GEMINI_API_KEY}`
    );
    if (!res.ok) throw new Error(`ListModels returned ${res.status}`);
    const data = await res.json();
    const models: string[] = (data.models ?? [])
      .filter((m: any) => (m.supportedGenerationMethods as string[])?.includes('generateContent'))
      .map((m: any) => (m.name as string).replace('models/', ''));
    console.log('[AI] Available Gemini models:', models);
    _availableGeminiModels = models.length > 0 ? models : GEMINI_MODELS_FALLBACK;
  } catch (err) {
    console.warn('[AI] ListModels failed, using fallback list:', err);
    _availableGeminiModels = GEMINI_MODELS_FALLBACK;
  }
  return _availableGeminiModels;
}

// Generic OpenAI-compatible chat completions caller (used by Groq, DeepSeek, etc.)
async function callChatCompletions(baseUrl: string, apiKey: string, model: string, prompt: string): Promise<string> {
  const response = await fetch(`${baseUrl}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model,
      messages: [{ role: 'user', content: prompt }],
      max_tokens: 4096,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any)?.error?.message || `${baseUrl} returned ${response.status}`);
  }
  const data = await response.json();
  return data.choices[0].message.content as string;
}

export interface ChatMessage {
  role: 'user' | 'model';
  parts: Array<{ text: string }>;
}

/**
 * Service for AI-powered features using Claude API.
 * Handles chat interactions, resource generation, gamification content creation,
 * and image generation. Communicates with backend API server.
 */
export class AIService {
  /**
   * Sends a chat message to the AI assistant and receives a response.
   * Maintains conversation history for context-aware responses.
   *
   * @param message - User's message/question to send to AI
   * @param history - Array of previous chat messages for context (defaults to empty)
   * @returns Promise resolving to AI's response text
   * @throws {Error} If API request fails or returns error
   *
   * @example
   * ```typescript
   * const history: ChatMessage[] = [];
   * const response = await AIService.sendChatMessage(
   *   'How can I improve student engagement?',
   *   history
   * );
   * console.log('AI Response:', response);
   * ```
   */
  static async sendChatMessage(message: string, history: ChatMessage[] = []): Promise<string> {
    // ── Direct Gemini path (no backend server required) ──────────────────────
    const model = GEMINI_API_KEY ? makeGeminiModel(GEMINI_MODELS_FALLBACK[0]) : null;
    if (model) {
      try {
        const chat = model.startChat({ history });
        const result = await chat.sendMessage(message);
        return result.response.text();
      } catch (error) {
        console.error('Gemini direct error:', error);
        throw error;
      }
    }

    // ── Backend / Cloud Function path ────────────────────────────────────────
    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, history }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get response');
      }

      const data = await response.json();
      return data.message;
    } catch (error) {
      console.error('Chat error:', error);
      throw error;
    }
  }

  /**
   * Generates educational content including tests, lesson plans, summaries, or images.
   * Uses AI to create customized educational materials based on parameters provided.
   *
   * @param request - GenerateResourceRequest object with content type and parameters
   * @returns Promise resolving to GenerateResourceResponse with content or image URL
   * @throws {Error} If API request fails or content generation fails
   *
   * @example
   * ```typescript
   * // Generate a test
   * const testResponse = await AIService.generateResource({
   *   contentType: 'test',
   *   parameters: {
   *     subject: 'Mathematics',
   *     gradeLevel: '8',
   *     topic: 'Linear Equations',
   *     numberOfQuestions: 15,
   *     includeAnswerKey: true
   *   }
   * });
   * console.log(testResponse.content);
   *
   * // Generate a lesson plan
   * const lessonResponse = await AIService.generateResource({
   *   contentType: 'lessonPlan',
   *   parameters: {
   *     subject: 'Science',
   *     gradeLevel: '6',
   *     topic: 'Photosynthesis',
   *     duration: '45',
   *     learningObjectives: 'Students will understand the process of photosynthesis'
   *   }
   * });
   * ```
   */
  /**
   * Generates a raw text response from the AI for a given prompt.
   * Uses generateContent (same proven path as generateResource) instead of the chat API.
   * Intended for structured generation tasks like JSON question lists.
   */
  static async generateText(prompt: string): Promise<string> {
    const errors: { label: string; err: unknown }[] = [];

    // 1. Google Gemini — discovers available models then tries them in order
    if (GEMINI_API_KEY) {
      let lastGeminiErr: unknown = null;
      for (const modelName of await getAvailableGeminiModels()) {
        try {
          const result = await makeGeminiModel(modelName)!.generateContent(prompt);
          return result.response.text();
        } catch (err) {
          lastGeminiErr = err;
          console.warn(`[AI] Gemini ${modelName} failed:`, err);
        }
      }
      errors.push({ label: 'Gemini', err: lastGeminiErr });
    }

    // 2. Groq — free tier, OpenAI-compatible (VITE_GROQ_API_KEY)
    if (GROQ_API_KEY) {
      try {
        return await callChatCompletions(
          'https://api.groq.com/openai/v1',
          GROQ_API_KEY,
          'llama-3.1-8b-instant',
          prompt,
        );
      } catch (err) {
        errors.push({ label: 'Groq', err });
        console.warn('[AI] Groq failed:', err);
      }
    }

    // 3. OpenRouter — tries several free models in order (VITE_OPENROUTER_API_KEY)
    if (OPENROUTER_API_KEY) {
      const openRouterModels = [
        'deepseek/deepseek-r1:free',
        'google/gemini-2.0-flash-exp:free',
        'meta-llama/llama-3.2-3b-instruct:free',
        'mistralai/mistral-7b-instruct:free',
      ];
      let lastOpenRouterErr: unknown = null;
      for (const model of openRouterModels) {
        try {
          return await callChatCompletions(
            'https://openrouter.ai/api/v1',
            OPENROUTER_API_KEY,
            model,
            prompt,
          );
        } catch (err) {
          lastOpenRouterErr = err;
          console.warn(`[AI] OpenRouter ${model} failed:`, err);
        }
      }
      errors.push({ label: 'OpenRouter', err: lastOpenRouterErr });
    }

    // 4. DeepSeek — free tier, OpenAI-compatible (VITE_DEEPSEEK_API_KEY)
    if (DEEPSEEK_API_KEY) {
      try {
        return await callChatCompletions(
          'https://api.deepseek.com/v1',
          DEEPSEEK_API_KEY,
          'deepseek-chat',
          prompt,
        );
      } catch (err) {
        errors.push({ label: 'DeepSeek', err });
        console.warn('[AI] DeepSeek failed:', err);
      }
    }

    // 5. Backend Cloud Function — server-side, no referrer restrictions (VITE_API_URL or same-origin)
    try {
      const response = await fetch(`${API_URL}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      if (!response.ok) throw new Error(`Backend returned ${response.status}`);
      const data = await response.json();
      return data.content;
    } catch (err) {
      errors.push({ label: 'Backend', err });
      console.warn('[AI] Backend fallback failed:', err);
    }

    // All providers failed — throw a combined message so the user can see each failure
    const messages = errors.map(({ label, err }) =>
      `${label}: ${err instanceof Error ? err.message : String(err)}`
    );
    throw new Error(messages.join(' | ') || 'No AI provider available');
  }

  static async generateResource(
    request: GenerateResourceRequest
  ): Promise<GenerateResourceResponse> {
    try {
      const { contentType, parameters } = request;

      // Build prompt based on content type
      let prompt = '';

      switch (contentType) {
        case 'test':
          prompt = `Create a comprehensive test for ${parameters.subject || 'the subject'} `;
          prompt += `for grade ${parameters.gradeLevel || 'level students'} `;
          prompt += `on the topic: ${parameters.topic || 'general concepts'}.\n\n`;
          prompt += `Generate ${parameters.numberOfQuestions || 10} questions.\n`;
          if (parameters.includeAnswerKey) {
            prompt += `Include a complete answer key at the end.\n`;
          }
          prompt += `Format: Clear questions with multiple choice or short answer format.`;
          break;

        case 'lessonPlan':
          prompt = `Create a detailed lesson plan for ${parameters.subject || 'the subject'} `;
          prompt += `for grade ${parameters.gradeLevel || 'level students'} `;
          prompt += `on the topic: ${parameters.topic || 'general concepts'}.\n\n`;
          prompt += `Duration: ${parameters.duration || '45'} minutes\n`;
          if (parameters.learningObjectives) {
            prompt += `Learning Objectives: ${parameters.learningObjectives}\n`;
          }
          prompt += `\nInclude the following sections:\n`;
          prompt += `1. Materials Needed\n`;
          prompt += `2. Introduction/Hook (5-10 min)\n`;
          prompt += `3. Main Activities (20-30 min)\n`;
          prompt += `4. Assessment\n`;
          prompt += `5. Closure`;
          break;

        case 'summary':
          prompt = `Summarize the topic: ${parameters.topic || 'the subject'}\n`;
          prompt += `Target Audience: ${parameters.targetAudience || 'students'}\n`;
          prompt += `Length: ${parameters.length === 'short' ? '2-3 paragraphs' : parameters.length === 'long' ? '6-8 paragraphs' : '4-5 paragraphs'}\n`;
          prompt += `Make it clear, engaging, and educational.`;
          break;

        default:
          throw new Error('Invalid content type');
      }

      // ── Direct Gemini path (tries versioned models, falls back to backend) ───
      if (GEMINI_API_KEY) {
        for (const modelName of await getAvailableGeminiModels()) {
          try {
            const result = await makeGeminiModel(modelName)!.generateContent(prompt);
            return { content: result.response.text() };
          } catch (err) {
            console.warn(`Direct Gemini ${modelName} failed, trying next:`, err);
          }
        }
        // All Gemini models failed — fall through to backend
      }

      // ── Backend path ───────────────────────────────────────────────────────
      const response = await fetch(`${API_URL}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate content');
      }

      const data = await response.json();
      return {
        content: data.content,
      };
    } catch (error) {
      console.error('Generate resource error:', error);
      throw error;
    }
  }

  /**
   * Checks the health status of the AI service backend.
   *
   * @returns Promise resolving to health status object
   */
  static async checkHealth(): Promise<{
    status: string;
    aiConfigured: boolean;
  }> {
    try {
      const response = await fetch(`${API_URL}/health`);
      if (!response.ok) {
        throw new Error('Health check failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Health check error:', error);
      return {
        status: 'error',
        aiConfigured: false,
      };
    }
  }
}
